/**
 * SEO Service for Marple Skin Care
 * Handles meta tags, structured data, and search optimization
 */

import { HelmetData } from 'react-helmet-async';

// Types
export interface SEOMetadata {
  title: string;
  description: string;
  keywords?: string[];
  canonical?: string;
  ogImage?: string;
  ogType?: 'website' | 'product' | 'article';
  twitterCard?: 'summary' | 'summary_large_image';
  noIndex?: boolean;
  structuredData?: Record<string, any>;
}

export interface ProductSEO extends SEOMetadata {
  product: {
    name: string;
    description: string;
    image: string;
    price: number;
    currency: string;
    availability: 'InStock' | 'OutOfStock' | 'PreOrder';
    brand: string;
    sku: string;
    category: string;
    rating?: {
      value: number;
      count: number;
    };
  };
}

export interface OrganizationSEO {
  name: string;
  url: string;
  logo: string;
  description: string;
  sameAs: string[];
  contactPoint?: {
    telephone: string;
    contactType: string;
    areaServed: string;
  };
}

// Default SEO configuration
const DEFAULT_SEO: SEOMetadata = {
  title: 'Marple Skin Care | AI-Powered Skincare & Beauty Products South Africa',
  description: 'Discover Marple Skin Care - South Africa\'s leading AI-powered skincare brand. Personalized skin analysis, premium products, and expert recommendations. Shop cleansers, serums, moisturizers & more. Free shipping over R500.',
  keywords: [
    'skincare South Africa',
    'beauty products',
    'skin analysis',
    'AI skincare',
    'cleanser',
    'serum',
    'moisturizer',
    'sunscreen',
    'anti-aging',
    'acne treatment',
    'vitamin C serum',
    'retinol',
    'hyaluronic acid',
    'niacinamide',
    'Marple',
  ],
  ogType: 'website',
  twitterCard: 'summary_large_image',
};

// Organization structured data
const ORGANIZATION_DATA: OrganizationSEO = {
  name: 'Marple Skin Care',
  url: 'https://marplecompany.co.za',
  logo: 'https://marplecompany.co.za/assets/logo.png',
  description: 'South Africa\'s leading AI-powered skincare brand offering personalized skin analysis and premium beauty products.',
  sameAs: [
    'https://www.facebook.com/marpleskincare',
    'https://www.instagram.com/marpleskincare',
    'https://www.tiktok.com/@marpleskincare',
    'https://www.youtube.com/@marpleskincare',
    'https://za.pinterest.com/marpleskincare/',
  ],
  contactPoint: {
    telephone: '+27-XXX-XXXX',
    contactType: 'Customer Service',
    areaServed: 'ZA',
  },
};

class SEOService {
  private baseUrl = 'https://marplecompany.co.za';

  // Generate page-specific SEO metadata
  generatePageSEO(
    page: string,
    params?: Record<string, any>
  ): SEOMetadata {
    const pageConfigs: Record<string, SEOMetadata> = {
      home: {
        title: 'Marple Skin Care | AI-Powered Skincare & Beauty Products South Africa',
        description: 'Transform your skin with AI-powered analysis and premium skincare products. Personalized routines, expert recommendations, and free shipping over R500.',
        ogImage: '/assets/og-home.jpg',
      },
      shop: {
        title: 'Shop Premium Skincare Products | Marple Skin Care',
        description: 'Browse our collection of cleansers, serums, moisturizers, and sunscreens. AI-matched products for your unique skin type. Shop now!',
        ogImage: '/assets/og-shop.jpg',
      },
      'skin-analysis': {
        title: 'Free AI Skin Analysis | Personalized Skincare Recommendations',
        description: 'Get your free AI-powered skin analysis. Upload photos and receive personalized product recommendations in minutes. Start your skincare journey today!',
        ogImage: '/assets/og-analysis.jpg',
      },
      product: {
        title: `${params?.name || 'Product'} | Marple Skin Care`,
        description: params?.description || 'Premium skincare product from Marple.',
        ogType: 'product',
        ogImage: params?.image,
      },
      blog: {
        title: `${params?.title || 'Blog'} | Marple Skin Care`,
        description: params?.excerpt || 'Expert skincare tips and advice from Marple.',
        ogType: 'article',
        ogImage: params?.image,
      },
      about: {
        title: 'About Marple | AI-Powered Skincare Brand South Africa',
        description: 'Learn about Marple\'s mission to revolutionize skincare through AI technology. Founded in South Africa, made for African skin.',
        ogImage: '/assets/og-about.jpg',
      },
      'agent-portal': {
        title: 'Marple Agent Portal | Join Our MLM Program',
        description: 'Join the Marple family as an independent agent. Earn commissions, build your team, and grow with South Africa\'s leading skincare brand.',
        noIndex: true, // Don't index agent portal
      },
    };

    const config = pageConfigs[page] || DEFAULT_SEO;
    
    return {
      ...DEFAULT_SEO,
      ...config,
      canonical: `${this.baseUrl}${params?.path || ''}`,
    };
  }

  // Generate Organization structured data (JSON-LD)
  generateOrganizationSchema(): Record<string, any> {
    return {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: ORGANIZATION_DATA.name,
      url: ORGANIZATION_DATA.url,
      logo: {
        '@type': 'ImageObject',
        url: ORGANIZATION_DATA.logo,
      },
      description: ORGANIZATION_DATA.description,
      sameAs: ORGANIZATION_DATA.sameAs,
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: ORGANIZATION_DATA.contactPoint?.telephone,
        contactType: ORGANIZATION_DATA.contactPoint?.contactType,
        areaServed: ORGANIZATION_DATA.contactPoint?.areaServed,
        availableLanguage: ['English', 'Afrikaans', 'Zulu'],
      },
    };
  }

  // Generate Product structured data (JSON-LD)
  generateProductSchema(product: ProductSEO['product']): Record<string, any> {
    const schema: Record<string, any> = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: product.name,
      image: product.image,
      description: product.description,
      sku: product.sku,
      brand: {
        '@type': 'Brand',
        name: product.brand,
      },
      offers: {
        '@type': 'Offer',
        url: `${this.baseUrl}/product/${product.sku}`,
        priceCurrency: product.currency,
        price: product.price.toString(),
        availability: `https://schema.org/${product.availability}`,
        seller: {
          '@type': 'Organization',
          name: 'Marple Skin Care',
        },
        shippingDetails: {
          '@type': 'OfferShippingDetails',
          shippingRate: {
            '@type': 'MonetaryAmount',
            value: '0',
            currency: 'ZAR',
          },
          shippingDestination: {
            '@type': 'DefinedRegion',
            addressCountry: 'ZA',
          },
        },
        hasMerchantReturnPolicy: {
          '@type': 'MerchantReturnPolicy',
          returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
          merchantReturnDays: 30,
          returnMethod: 'https://schema.org/ReturnByMail',
          returnFees: 'https://schema.org/FreeReturn',
        },
      },
    };

    if (product.rating) {
      schema.aggregateRating = {
        '@type': 'AggregateRating',
        ratingValue: product.rating.value.toString(),
        reviewCount: product.rating.count.toString(),
      };
    }

    return schema;
  }

  // Generate Local Business structured data
  generateLocalBusinessSchema(): Record<string, any> {
    return {
      '@context': 'https://schema.org',
      '@type': 'BeautySalon',
      name: 'Marple Skin Care',
      image: 'https://marplecompany.co.za/assets/store.jpg',
      '@id': 'https://marplecompany.co.za',
      url: 'https://marplecompany.co.za',
      telephone: '+27-XXX-XXXX',
      priceRange: '$$',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Your Street Address',
        addressLocality: 'Johannesburg',
        addressRegion: 'GP',
        postalCode: '2000',
        addressCountry: 'ZA',
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: -26.2041,
        longitude: 28.0473,
      },
      openingHoursSpecification: [
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
          opens: '09:00',
          closes: '17:00',
        },
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: 'Saturday',
          opens: '09:00',
          closes: '13:00',
        },
      ],
      sameAs: ORGANIZATION_DATA.sameAs,
    };
  }

  // Generate Breadcrumb structured data
  generateBreadcrumbSchema(items: Array<{ name: string; url: string }>): Record<string, any> {
    return {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: items.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.name,
        item: `${this.baseUrl}${item.url}`,
      })),
    };
  }

  // Generate FAQ structured data
  generateFAQSchema(faqs: Array<{ question: string; answer: string }>): Record<string, any> {
    return {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((faq) => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer,
        },
      })),
    };
  }

  // Generate HowTo structured data (for skin analysis)
  generateHowToSchema(): Record<string, any> {
    return {
      '@context': 'https://schema.org',
      '@type': 'HowTo',
      name: 'How to Get Your Free AI Skin Analysis',
      description: 'Follow these simple steps to get personalized skincare recommendations from our AI.',
      totalTime: 'PT5M',
      estimatedCost: {
        '@type': 'MonetaryAmount',
        currency: 'ZAR',
        value: '0',
      },
      step: [
        {
          '@type': 'HowToStep',
          name: 'Take Photos',
          text: 'Take clear photos of your face in good lighting - front, left, and right views.',
          url: 'https://marplecompany.co.za/skin-analysis#step1',
        },
        {
          '@type': 'HowToStep',
          name: 'Answer Questions',
          text: 'Complete a short questionnaire about your skin concerns and current routine.',
          url: 'https://marplecompany.co.za/skin-analysis#step2',
        },
        {
          '@type': 'HowToStep',
          name: 'Get Analysis',
          text: 'Our AI analyzes your skin and generates personalized recommendations.',
          url: 'https://marplecompany.co.za/skin-analysis#step3',
        },
        {
          '@type': 'HowToStep',
          name: 'Shop Your Routine',
          text: 'Browse AI-matched products and build your perfect skincare routine.',
          url: 'https://marplecompany.co.za/skin-analysis#step4',
        },
      ],
    };
  }

  // Generate WebSite structured data (for search box)
  generateWebSiteSchema(): Record<string, any> {
    return {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      url: this.baseUrl,
      name: 'Marple Skin Care',
      description: 'AI-powered skincare products and analysis',
      potentialAction: {
        '@type': 'SearchAction',
        target: {
          '@type': 'EntryPoint',
          urlTemplate: `${this.baseUrl}/search?q={search_term_string}`,
        },
        'query-input': 'required name=search_term_string',
      },
    };
  }

  // Generate Article structured data (for blog)
  generateArticleSchema(article: {
    title: string;
    description: string;
    image: string;
    publishedAt: string;
    modifiedAt?: string;
    author: string;
    category: string;
  }): Record<string, any> {
    return {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.title,
      description: article.description,
      image: article.image,
      datePublished: article.publishedAt,
      dateModified: article.modifiedAt || article.publishedAt,
      author: {
        '@type': 'Organization',
        name: article.author,
      },
      publisher: {
        '@type': 'Organization',
        name: 'Marple Skin Care',
        logo: {
          '@type': 'ImageObject',
          url: 'https://marplecompany.co.za/assets/logo.png',
        },
      },
      articleSection: article.category,
    };
  }

  // Generate meta tags HTML
  generateMetaTags(metadata: SEOMetadata): string {
    const keywords = metadata.keywords?.join(', ') || '';
    
    return `
      <!-- Primary Meta Tags -->
      <title>${metadata.title}</title>
      <meta name="title" content="${metadata.title}" />
      <meta name="description" content="${metadata.description}" />
      ${keywords ? `<meta name="keywords" content="${keywords}" />` : ''}
      <meta name="robots" content="${metadata.noIndex ? 'noindex, nofollow' : 'index, follow'}" />
      <meta name="language" content="English" />
      <meta name="revisit-after" content="7 days" />
      <meta name="author" content="Marple Skin Care" />
      
      <!-- Canonical URL -->
      ${metadata.canonical ? `<link rel="canonical" href="${metadata.canonical}" />` : ''}
      
      <!-- Open Graph / Facebook -->
      <meta property="og:type" content="${metadata.ogType}" />
      <meta property="og:url" content="${metadata.canonical}" />
      <meta property="og:title" content="${metadata.title}" />
      <meta property="og:description" content="${metadata.description}" />
      ${metadata.ogImage ? `<meta property="og:image" content="${metadata.ogImage}" />` : ''}
      <meta property="og:site_name" content="Marple Skin Care" />
      <meta property="og:locale" content="en_ZA" />
      
      <!-- Twitter -->
      <meta property="twitter:card" content="${metadata.twitterCard}" />
      <meta property="twitter:url" content="${metadata.canonical}" />
      <meta property="twitter:title" content="${metadata.title}" />
      <meta property="twitter:description" content="${metadata.description}" />
      ${metadata.ogImage ? `<meta property="twitter:image" content="${metadata.ogImage}" />` : ''}
      
      <!-- Geo Tags -->
      <meta name="geo.region" content="ZA" />
      <meta name="geo.placename" content="South Africa" />
      
      <!-- Mobile -->
      <meta name="theme-color" content="#7C3AED" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
      <meta name="apple-mobile-web-app-title" content="Marple Skin Care" />
    `.trim();
  }

  // Generate structured data script tag
  generateStructuredDataScript(data: Record<string, any>): string {
    return `<script type="application/ld+json">${JSON.stringify(data)}</script>`;
  }

  // Validate SEO metadata
  validateMetadata(metadata: SEOMetadata): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!metadata.title || metadata.title.length < 10 || metadata.title.length > 70) {
      errors.push('Title should be between 10-70 characters');
    }

    if (!metadata.description || metadata.description.length < 50 || metadata.description.length > 160) {
      errors.push('Description should be between 50-160 characters');
    }

    if (metadata.keywords && metadata.keywords.length > 10) {
      errors.push('Too many keywords (max 10 recommended)');
    }

    return {
      valid: errors.length === 0,
      errors,
    };
  }
}

export const seoService = new SEOService();
export default seoService;
